import { cn } from './primitives'

/**
 * Small hand-drawn SVG scenes for empty/error/loading moments — line art in the app's own ink and one
 * accent, so they sit quietly on the white surfaces. Motion is gentle (float, draw, pulse) and switches
 * off under prefers-reduced-motion via the global rule in tokens.css.
 */
export type IllustrationKind = 'empty' | 'search' | 'error' | 'offline' | 'locked' | 'success' | 'truck' | 'chart'

const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

const scenes: Record<IllustrationKind, (a: string) => JSX.Element> = {
  empty: (a) => (
    <g {...common}>
      <g className="illus-float">
        <path d="M32 46 64 34l32 12-32 12z" fill="var(--surface)" />
        <path d="M32 46v30l32 12V58zM96 46v30L64 88V58" />
        <path d="M48 40l32 12v10l-8-3v-8" stroke={a} />
      </g>
      <circle cx="26" cy="30" r="2" fill={a} stroke="none" className="illus-dot" />
      <circle cx="104" cy="26" r="2.5" fill={a} stroke="none" className="illus-dot illus-dot-2" />
      <circle cx="98" cy="92" r="1.8" fill={a} stroke="none" className="illus-dot illus-dot-3" />
      <path d="M24 100h80" strokeDasharray="3 6" opacity=".5" />
    </g>
  ),
  search: (a) => (
    <g {...common}>
      <g className="illus-float">
        <circle cx="58" cy="54" r="22" fill="var(--surface)" />
        <circle cx="58" cy="54" r="22" className="illus-draw" />
        <path d="M74 70l18 18" strokeWidth="3" />
        <path d="M48 54h20M58 44v20" stroke={a} opacity=".9" />
      </g>
      <path d="M24 100h80" strokeDasharray="3 6" opacity=".5" />
      <circle cx="98" cy="30" r="2" fill={a} stroke="none" className="illus-dot" />
    </g>
  ),
  error: (a) => (
    <g {...common}>
      <g className="illus-float">
        <path d="M32 46 64 34l32 12-32 12z" fill="var(--surface)" />
        <path d="M32 46v30l32 12V58zM96 46v30L64 88V58" />
        <path d="M60 24l8-6M70 26l6-8M52 22l2-9" stroke={a} className="illus-pulse" />
      </g>
      <circle cx="98" cy="86" r="12" fill="var(--surface)" stroke={a} />
      <path d="M98 80v7M98 91v.5" stroke={a} strokeWidth="2.2" />
      <path d="M24 100h64" strokeDasharray="3 6" opacity=".5" />
    </g>
  ),
  offline: (a) => (
    <g {...common}>
      <g className="illus-float">
        <path d="M40 76h50a14 14 0 0 0 2-27.9A20 20 0 0 0 54 42a16 16 0 0 0-14 34z" fill="var(--surface)" />
        <path d="M30 40l68 46" stroke={a} strokeWidth="2.2" />
      </g>
      <path d="M24 100h80" strokeDasharray="3 6" opacity=".5" />
    </g>
  ),
  locked: (a) => (
    <g {...common}>
      <g className="illus-float">
        <rect x="42" y="52" width="44" height="34" rx="6" fill="var(--surface)" />
        <path d="M50 52v-8a14 14 0 0 1 28 0v8" />
        <circle cx="64" cy="68" r="3.5" fill={a} stroke="none" />
        <path d="M64 71v6" stroke={a} strokeWidth="2.2" />
      </g>
      <path d="M24 100h80" strokeDasharray="3 6" opacity=".5" />
    </g>
  ),
  success: (a) => (
    <g {...common}>
      <g className="illus-float">
        <path d="M32 46 64 34l32 12-32 12z" fill="var(--surface)" />
        <path d="M32 46v30l32 12V58zM96 46v30L64 88V58" />
      </g>
      <circle cx="96" cy="30" r="14" fill="var(--surface)" stroke={a} />
      <path d="M89 30l5 5 9-10" stroke={a} strokeWidth="2.4" className="illus-draw" />
      <path d="M24 100h80" strokeDasharray="3 6" opacity=".5" />
    </g>
  ),
  truck: (a) => (
    <g {...common}>
      <g className="illus-drive">
        <path d="M18 44h52v34H18z" fill="var(--surface)" />
        <path d="M70 54h18l14 14v10H70z" fill="var(--surface)" />
        <path d="M18 44h52v34H18zM70 54h18l14 14v10H70zM88 54v14h14" />
        <circle cx="34" cy="80" r="7" fill="var(--surface)" />
        <circle cx="34" cy="80" r="7" className="illus-spin" strokeDasharray="4 4" />
        <circle cx="84" cy="80" r="7" fill="var(--surface)" />
        <circle cx="84" cy="80" r="7" className="illus-spin" strokeDasharray="4 4" />
        <path d="M28 56h24M28 64h16" stroke={a} />
      </g>
      <path d="M10 92h108" strokeDasharray="6 8" opacity=".55" className="illus-road" />
      <path d="M14 34h10M8 28h4" stroke={a} opacity=".7" className="illus-dot" />
    </g>
  ),
  chart: (a) => (
    <g {...common}>
      <path d="M24 88h80" opacity=".6" />
      <rect x="34" y="60" width="12" height="28" rx="2" fill="var(--surface)" className="illus-bar" />
      <rect x="56" y="44" width="12" height="44" rx="2" fill="var(--surface)" className="illus-bar illus-bar-2" />
      <rect x="78" y="52" width="12" height="36" rx="2" fill="var(--surface)" className="illus-bar illus-bar-3" />
      <path d="M32 50l24-14 22 8 22-18" stroke={a} strokeWidth="2.2" className="illus-draw" />
      <circle cx="100" cy="26" r="3" fill={a} stroke="none" className="illus-dot" />
    </g>
  ),
}

export function Illustration({ kind, size = 128, className }: { kind: IllustrationKind; size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 128 112" width={size} height={Math.round(size * 0.875)} className={cn('keep-color text-fg-subtle', className)} aria-hidden>
      {scenes[kind]('var(--accent)')}
    </svg>
  )
}
